import Card from 'components/card';
import BuyerMarketPlaceCard from './components/OfferCard';
import LeadMarketplace from '../leadMarketplace';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { getImage } from 'variables/images';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { allChannels } from 'variables/industries';
import { useIndustries } from 'services/industryService';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import SignUpModal from './components/SignUpModal';
import WelcomeModal from './components/WelcomeModal';
import { MdEuro, MdOutlineStorefront, MdOutlineGpsFixed, MdFormatListBulleted, MdBarChart } from 'react-icons/md';
import { formatPrice } from 'utils/price';

const MySwal = withReactContent(Swal);

const priceOptions = [
    { value: '1-10', label: 'De 1 à 10€' },
    { value: '10-20', label: 'De 10 à 20€' },
    { value: '20-30', label: 'De 20 à 30€' },
    { value: '30-40', label: 'De 30 à 40€' },
    { value: '40-50', label: 'De 40 à 50€' },
    { value: '50-60', label: 'De 50 à 60€' },
    { value: '60-70', label: 'De 60 à 70€' },
    { value: '70-80', label: 'De 70 à 80€' },
    { value: '80-90', label: 'De 80 à 90€' },
    { value: '90-100', label: 'De 90 à 100€' },
    { value: '100+', label: 'Plus de 100€' },
];

const Dashboard = () => {
    const { industries: industriesData, loading: industriesLoading } = useIndustries();
    const [offer, setOffer] = useState([]);
    const [user, setUser] = useState(null);
    const [walletBalance, setWalletBalance] = useState<number | null>(null);
    const [selectedIndustry, setSelectedIndustry] = useState('');
    const [selectedPrice, setSelectedPrice] = useState('all');
    const [selectedChannel, setSelectedChannel] = useState('all');
    const [searchOfferNumber, setSearchOfferNumber] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [hasInteracted, setHasInteracted] = useState(false);
    const [showSignUpModal, setShowSignUpModal] = useState(false);
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    const nav = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const urlOfferNumber = searchParams.get('offerNumber');
    const isPrefiltered = !!urlOfferNumber;
    const [orderType, setOrderType] = useState<'order' | 'purchase-order'>(
        searchParams.get('orderType') === 'purchase-order' ? 'purchase-order' : 'order'
    );

    // Re-sync when the query param changes via an in-app nav (e.g. sidebar quick actions)
    // while already sitting on /dashboard — a route with only a search-string change doesn't
    // remount the component, so the initial useState value above wouldn't otherwise update.
    useEffect(() => {
        setOrderType(searchParams.get('orderType') === 'purchase-order' ? 'purchase-order' : 'order');
    }, [searchParams]);

    // Cards also write the param back so a later sidebar click (even to a URL that's
    // otherwise unchanged) still reflects the correct selection.
    const selectOrderType = (type: 'order' | 'purchase-order') => {
        setOrderType(type);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (type === 'purchase-order') next.set('orderType', 'purchase-order');
            else next.delete('orderType');
            return next;
        }, { replace: true });
    };

    // Guest user detection
    const isGuest = user?.email?.includes('guest-') ?? false;

    const loginGoogle = (token: string) => {
        axios
            .post(`${CONFIG.apiUrl}/auth/google`, { token })
            .then((response: any) => {
                if (response.data.token) {
                    localStorage.setItem('token', response.data.token);
                    localStorage.setItem('user-type', response.data.type);
                    window.location.href = '/dashboard';
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    };

    const handleGuestInteraction = () => {
        if (
            user &&
            user.email &&
            user.email.includes('guest') &&
            !hasInteracted
        ) {
            setHasInteracted(true);
            openGuestPopUp();
        }
    };

    const initOffer = () => {
        const token = localStorage.getItem('token');
        const config = {
            headers: { Authorization: `Bearer ${token}` },
        };
        axios
            .get(`${CONFIG.apiUrl}/seller-offer/marketplace`, config)
            .then((res) => {
                const offers = res.data;
                setOffer(offers);
                setIsLoading(false);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const getOffersIndustry = () => {
        return industriesData.map(industry => industry.name);
    };

    const updateProfil = async (email: string) => {
        const token = localStorage.getItem('token');
        const config = {
            headers: { Authorization: `Bearer ${token}` },
        };
        const data = { email };
        try {
            const result: any = await axios.put(
                `${CONFIG.apiUrl}/user`,
                data,
                config
            );
            if (result.status === 200) {
                return true;
            } else {
                return false;
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            return false;
        }
    };

    const openGuestPopUp = () => {
        //setShowSignUpModal(true);
        setShowWelcomeModal(true);
    };

    const initUser = () => {
        const token = localStorage.getItem('token');
        const config = {
            headers: { Authorization: `Bearer ${token}` },
        };
        axios
            .get(`${CONFIG.apiUrl}/user/me`, config)
            .then((res) => {
                const user = res.data;
                if (user.kyc_url) {
                    setTimeout(() => {
                        window.location.href = user.kyc_url;
                    }, 3000);
                }
                setUser(user);
            })
            .catch((err) => {
                console.log(err);
                window.location.href = '/auth/sign-in';
            });
    };

    const initWallet = () => {
        const token = localStorage.getItem('token');
        axios
            .get(`${CONFIG.apiUrl}/user/wallet`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => setWalletBalance(res.data.walletBalance ?? 0))
            .catch(() => {});
    };

    useEffect(() => {
        initOffer();
        initUser();
        initWallet();
        if (urlOfferNumber) {
            setSearchOfferNumber(urlOfferNumber);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearPrefilter = () => {
        setSearchOfferNumber('');
        nav(window.location.pathname);
    };

    const Headline = () => {
        return (
            <div className="flex flex-col gap-4 lg:flex-row">
                {/* Left: info card */}
                <div className="flex-1 rounded-xl border border-leadrs-stroke bg-white p-6">
                    <div className="flex items-start gap-4">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-leadrs-brown-soft">
                            <MdOutlineStorefront className="h-5 w-5 text-leadrs-brown-focus" />
                        </div>
                        <div>
                            <h2 className="font-crimson text-2xl font-semibold leading-tight text-leadrs-text">
                                Nos offres
                            </h2>
                            <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                                Parcourez toutes nos offres disponibles et choisissez celles qui correspondent le mieux à vos besoins
                            </p>
                        </div>
                    </div>
                </div>

                {/* Right: wallet + offer sur-mesure */}
                <div className="flex flex-col gap-4 lg:w-72">
                    {/* Wallet card */}
                    <div className="rounded-xl border border-leadrs-stroke bg-white p-5">
                        <div className="flex items-center gap-4">
                            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-leadrs-yellow-soft">
                                <MdEuro className="h-5 w-5 text-leadrs-yellow-focus" />
                            </div>
                            <div>
                                <p className="font-geist text-sm text-leadrs-text-soft">Solde disponible</p>
                                <p className="font-geist text-xl font-bold text-leadrs-text">
                                    {walletBalance !== null ? `${walletBalance.toFixed(2)}€` : '—'}
                                </p>
                                <span
                                    onClick={() => nav('/admin/wallet/topup')}
                                    className="cursor-pointer font-geist text-sm text-leadrs-brand underline hover:opacity-80"
                                >
                                    + Ajouter des crédits
                                </span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        );
    };

    const InfoRow = ({ label, value }: { label: string; value: string }) => (
        <div className="flex items-center justify-between gap-3 py-2.5">
            <span className="font-geist text-sm text-leadrs-text-soft">{label}</span>
            <span className="font-geist text-sm font-medium text-leadrs-text">{value}</span>
        </div>
    );

    type OfferTypeCard = {
        type: 'order' | 'purchase-order';
        title: string;
        subtitle: string;
        bgSoft: string;
        bgSoftLight: string;
        textFocus: string;
        bottomDesc: string;
        icon: JSX.Element;
        rows: { label: string; value: string }[];
        stepsLabel: string;
        steps: { title: string; desc: string }[];
    };

    const offerTypeCards: OfferTypeCard[] = [
        {
            type: 'order',
            title: 'Commandes',
            subtitle: 'Pour un besoin récurrent',
            bgSoft: 'bg-leadrs-yellow-soft',
            bgSoftLight: 'bg-leadrs-yellow-soft/20',
            textFocus: 'text-leadrs-yellow-focus',
            bottomDesc: 'Des offres au prix fixe, livrées en continu.',
            icon: <MdFormatListBulleted className="h-5 w-5 text-leadrs-yellow-focus" />,
            rows: [
                { label: 'Le prix', value: "Fixe, connu à l'avance" },
                { label: 'Vous achetez', value: 'Un volume de leads à venir' },
                { label: 'Livraison', value: 'En continu, au fil de la collecte' },
            ],
            stepsLabel: 'Deux façons de commander',
            steps: [
                { title: 'Sélectionnez une offre', desc: 'Prix, volume et canaux déjà fixés. Livraison dès validation.' },
                { title: 'Ou faites votre commande', desc: 'Décrivez votre besoin sur mesure. Réponse sous 48 h.' },
            ],
        },
        {
            type: 'purchase-order',
            title: "Ordres d'achat",
            subtitle: 'Pour un besoin immédiat',
            bgSoft: 'bg-leadrs-green-soft',
            bgSoftLight: 'bg-leadrs-green-soft/20',
            textFocus: 'text-leadrs-green-focus',
            bottomDesc: "Le marché en direct, au prix de l'instant.",
            icon: <MdBarChart className="h-5 w-5 text-leadrs-green-focus" />,
            rows: [
                { label: 'Le prix', value: 'Du marché, il baisse d’heure en heure' },
                { label: 'Vous achetez', value: 'Un lead précis, déjà collecté' },
                { label: 'Livraison', value: 'Immédiate, dès le paiement' },
            ],
            stepsLabel: "Deux façons d'acheter",
            steps: [
                { title: 'Achetez un lead en direct', desc: "Le prix affiché baisse d'heure en heure. Premier arrivé, premier servi." },
                { title: 'Ou passez un ordre permanent', desc: "Il se déclenche seul dès qu'un lead atteint votre prix." },
            ],
        },
    ];

    const OfferTypeSelector = () => {
        const selected = offerTypeCards.find((c) => c.type === orderType) || offerTypeCards[0];

        return (
            <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {offerTypeCards.map((card) => {
                        const isSelected = card.type === orderType;
                        return (
                            <button
                                key={card.type}
                                type="button"
                                onClick={() => selectOrderType(card.type)}
                                className={`rounded-2xl ${card.bgSoftLight} p-6 text-left transition-all ${
                                    isSelected ? 'border-2 border-leadrs-brand' : 'border-2 border-transparent'
                                }`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${card.bgSoft}`}>
                                            {card.icon}
                                        </div>
                                        <div>
                                            <h3 className="font-crimson text-xl font-semibold text-leadrs-text">{card.title}</h3>
                                            <p className="font-geist text-sm text-leadrs-text-soft">{card.subtitle}</p>
                                        </div>
                                    </div>
                                    <span
                                        className={`mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 bg-white ${
                                            isSelected ? 'border-leadrs-text' : 'border-leadrs-stroke'
                                        }`}
                                    >
                                        {isSelected && <span className="h-2.5 w-2.5 rounded-full bg-leadrs-text" />}
                                    </span>
                                </div>

                                <div className="mt-5 divide-y divide-leadrs-stroke border-t border-leadrs-stroke">
                                    {card.rows.map((row) => (
                                        <InfoRow key={row.label} label={row.label} value={row.value} />
                                    ))}
                                </div>

                                <p className="mt-5 font-geist text-sm text-leadrs-text-soft">{card.stepsLabel}</p>
                                <div className="mt-3 space-y-3">
                                    {card.steps.map((step, i) => (
                                        <div key={step.title} className="flex items-start gap-3">
                                            <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full font-geist text-xs font-semibold ${card.bgSoft} ${card.textFocus}`}>
                                                {i + 1}
                                            </span>
                                            <div>
                                                <p className="font-geist text-sm font-semibold text-leadrs-text">{step.title}</p>
                                                <p className="mt-0.5 font-geist text-sm text-leadrs-text-soft">{step.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </button>
                        );
                    })}
                </div>

                <div className={`flex flex-wrap items-center justify-between gap-4 rounded-2xl ${selected.bgSoft} p-5`}>
                    <div>
                        <span className="font-crimson text-lg font-semibold text-leadrs-text">{selected.title}</span>
                        <span className="ml-2 font-geist text-sm text-leadrs-text-soft">{selected.bottomDesc}</span>
                    </div>
                    <button
                        onClick={() => nav(selected.type === 'order' ? '/create-order' : '/purchase-order')}
                        className="flex items-center gap-2 rounded-xl bg-leadrs-text px-5 py-3 font-geist text-sm font-semibold text-white transition hover:opacity-90"
                    >
                        {selected.icon}
                        {selected.type === 'order' ? 'Faire une commande' : "Faire un ordre d'achat"}
                    </button>
                </div>
            </div>
        );
    };

    if (industriesLoading || isLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="text-xl font-bold text-navy-700 dark:text-white">
                    {industriesLoading ? 'Chargement des industries...' : 'Chargement...'}
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col rounded-[20px] xl:flex-row ">
            <div className="h-full w-full rounded-[20px]">
                {/* left side */}
                <div className="col-span-9 h-full w-full rounded-t-2xl xl:col-span-6">
                    {/* Your Transfers & tables */}
                    <div className="grid w-full grid-cols-12 gap-2">
                        <div className="col-span-12">
                            <div className={'h-full w-full px-1'}>
                                <Headline />
                                <div className="mt-4">
                                    <OfferTypeSelector />
                                </div>
                                {orderType === 'purchase-order' ? (
                                    <div className="mt-6">
                                        <LeadMarketplace />
                                    </div>
                                ) : (
                                    <>
                                        <p className="mb-1 mt-6 font-geist text-[11px] font-semibold uppercase tracking-widest text-leadrs-text-soft">
                                            Offres existantes
                                        </p>
                                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                                            <div className="flex flex-wrap items-center gap-2">
                                                {/* Industry filter */}
                                                <select
                                                    onChange={(e: any) => setSelectedIndustry(e.target.value)}
                                                    className="h-10 rounded-xl border border-leadrs-stroke bg-white px-3 font-geist text-sm text-leadrs-text outline-none transition focus:border-leadrs-brand lg:w-[200px]"
                                                >
                                                    <option value="">Toutes les offres</option>
                                                    {getOffersIndustry().map((industry: any) => (
                                                        <option key={industry} value={industry}>{industry}</option>
                                                    ))}
                                                </select>
                                                {/* Pricing filter */}
                                                <select
                                                    onChange={(e: any) => setSelectedPrice(e.target.value)}
                                                    className="h-10 rounded-xl border border-leadrs-stroke bg-white px-3 font-geist text-sm text-leadrs-text outline-none transition focus:border-leadrs-brand lg:w-[210px]"
                                                >
                                                    <option value="all">Tous les prix par lead</option>
                                                    {priceOptions.map((option) => (
                                                        <option key={option.value} value={option.value}>{option.label}</option>
                                                    ))}
                                                </select>
                                                {/* Channel filter */}
                                                <select
                                                    onChange={(e: any) => setSelectedChannel(e.target.value)}
                                                    className="h-10 rounded-xl border border-leadrs-stroke bg-white px-3 font-geist text-sm text-leadrs-text outline-none transition focus:border-leadrs-brand lg:w-[260px]"
                                                >
                                                    <option value="all">Tous les canaux d'acquisition</option>
                                                    {allChannels.map((channel) => (
                                                        <option key={channel} value={channel}>{channel}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        {isLoading ? (
                                            <>
                                                <div className="h-50 mt-[100px] flex items-center justify-center">
                                                    <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-leadrs-brand"></div>
                                                </div>
                                                <p className="mt-3 text-center font-geist text-sm text-leadrs-text-soft">
                                                    Récupération des offres...
                                                </p>
                                            </>
                                        ) : (
                                            <div className="mt-3 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
                                                {/* First batch of offer cards (1 when prefiltered, all otherwise) */}
                                                {offer
                                                    ?.filter((item) => item.industry === selectedIndustry || !selectedIndustry)
                                                    .filter((item) => {
                                                        if (selectedPrice === 'all') return true;
                                                        const [min, max] = selectedPrice.split('-');
                                                        const price = item.buyerPrice;
                                                        if (max) return price >= parseInt(min) && price <= parseInt(max);
                                                        return price >= parseInt(min);
                                                    })
                                                    .filter((item) => {
                                                        if (selectedChannel === 'all') return true;
                                                        return item.channels.some((channel: string) => channel === selectedChannel);
                                                    })
                                                    .filter((item) => {
                                                        if (!searchOfferNumber) return true;
                                                        return String(item.offerBuyerNumber).includes(searchOfferNumber);
                                                    })
                                                    .sort(() => Math.random() - 0.5)
                                                    .slice(0, isPrefiltered ? 1 : undefined)
                                                    .map((item) => (
                                                        <BuyerMarketPlaceCard
                                                            key={item._id}
                                                            channels={item.channels}
                                                            offerNumber={item.offerBuyerNumber}
                                                            industry={item.industry}
                                                            subIndustry={item.subIndustry}
                                                            targetPrice={item.targetPrice}
                                                            weeklyQuantity={Number(item.weeklyQuantity)}
                                                            leadsGenerated={Number(item.leadsGenerated)}
                                                            otherIndustry={item.otherIndustry}
                                                            buyerPrice={formatPrice(item.buyerPrice)}
                                                            user={user}
                                                            status={item.status}
                                                            image={getImage(item.industry, item.subIndustry)}
                                                            isGuest={isGuest}
                                                            onGuestClick={() => setShowWelcomeModal(true)}
                                                            onClick={() => {
                                                                isGuest ? openGuestPopUp() : nav(`/market-offer/${item._id}`);
                                                            }}
                                                        />
                                                    ))}

                                                {/* Prefiltered banner — spans 3 cols, replaces placeholder cards */}
                                                {isPrefiltered && (
                                                    <div className="col-span-1 flex flex-col justify-center rounded-xl border border-leadrs-purple-soft bg-leadrs-purple-soft p-8 md:col-span-1 xl:col-span-3">
                                                        <div className="flex items-start gap-4">
                                                            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-leadrs-purple-focus">
                                                                <MdOutlineGpsFixed className="h-6 w-6 text-white" />
                                                            </div>
                                                            <div className="flex-1">
                                                                <p className="mb-1 font-geist text-xs font-semibold uppercase tracking-wider text-leadrs-purple-focus">Offre présélectionnée</p>
                                                                <h2 className="mb-2 font-geist text-xl font-bold text-leadrs-purple-focus">
                                                                    Cette offre a été partagée avec vous
                                                                </h2>
                                                                <p className="mb-5 font-geist text-sm leading-relaxed text-leadrs-purple-focus opacity-70">
                                                                    Un lien vous a dirigé directement vers cette offre. Des dizaines d'autres offres vous attendent sur notre marketplace.
                                                                </p>
                                                                <button
                                                                    onClick={clearPrefilter}
                                                                    className="inline-flex items-center gap-2 rounded-xl bg-leadrs-purple-focus px-5 py-2.5 font-geist text-sm font-medium text-white transition hover:opacity-80"
                                                                >
                                                                    Voir toutes les offres →
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {!isPrefiltered && offer
                                                    ?.filter((item) => item.industry === selectedIndustry || !selectedIndustry)
                                                    .filter((item) => {
                                                        if (selectedPrice === 'all') return true;
                                                        const [min, max] = selectedPrice.split('-');
                                                        const price = item.buyerPrice;
                                                        if (max) return price >= parseInt(min) && price <= parseInt(max);
                                                        return price >= parseInt(min);
                                                    })
                                                    .filter((item) => {
                                                        if (selectedChannel === 'all') return true;
                                                        return item.channels.some((channel: string) => channel === selectedChannel);
                                                    })
                                                    .filter((item) => {
                                                        if (!searchOfferNumber) return true;
                                                        return String(item.offerBuyerNumber).includes(searchOfferNumber);
                                                    })
                                                    .slice(3)
                                                    .map((item) => (
                                                        <BuyerMarketPlaceCard
                                                            key={item._id}
                                                            channels={item.channels}
                                                            offerNumber={item.offerBuyerNumber}
                                                            industry={item.industry}
                                                            subIndustry={item.subIndustry}
                                                            targetPrice={item.targetPrice}
                                                            weeklyQuantity={Number(item.weeklyQuantity)}
                                                            leadsGenerated={Number(item.leadsGenerated)}
                                                            otherIndustry={item.otherIndustry}
                                                            buyerPrice={formatPrice(item.buyerPrice)}
                                                            user={user}
                                                            status={item.status}
                                                            image={getImage(item.industry, item.subIndustry)}
                                                            isGuest={isGuest}
                                                            onGuestClick={() => setShowSignUpModal(true)}
                                                            onClick={() => { nav(`/market-offer/${item._id}`); }}
                                                        />
                                                    ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <SignUpModal
                isOpen={showSignUpModal}
                onClose={(accountCreated: boolean) => {
                    setShowSignUpModal(false);
                    if (accountCreated) {
                        initUser(); // Refetch user data to reveal prices
                        setShowWelcomeModal(true); // Show welcome modal after successful registration
                    }
                }}
            />
            <WelcomeModal
                isOpen={showWelcomeModal}
                onClose={() => setShowWelcomeModal(false)}
                onSignUp={() => {
                    setShowWelcomeModal(false);
                    setShowSignUpModal(true);
                }}
            />
        </div>
    );
};

export default Dashboard;
